import { parseNumericValue } from "./formatNumber";

export const RESOURCE_LABELS = {
  ambients: "Hectáreas para ambientar",
  reseller_ambients_ha: "Hectáreas para ambientar - Revender",
  reports_ndvi_ha: "Hectáreas para reportes",
  reseller_reports_ndvi_ha: "Hectáreas para reportes - Revender",
};

export const USAGE_ONLY_RESOURCE_LABELS = {
  ha: "Hectáreas activas en lotes",
};

export const RESOURCE_ORDER = [
  "ambients",
  "reseller_ambients_ha",
  "reports_ndvi_ha",
  "reseller_reports_ndvi_ha",
];

const KNOWN_RESOURCE_IDS = Object.keys(RESOURCE_LABELS);

export const normalizeResourceId = (resourceId) => (
  resourceId
);

export const getResourceLabel = (resourceId) => (
  RESOURCE_LABELS[resourceId] || RESOURCE_LABELS[normalizeResourceId(resourceId)]
);

export const getUsageResourceLabel = (resourceId) => (
  getResourceLabel(resourceId) || USAGE_ONLY_RESOURCE_LABELS[normalizeResourceId(resourceId)]
);

export const isManagedResource = (resourceId) => (
  KNOWN_RESOURCE_IDS.includes(resourceId)
);

export const sortManagedResources = (resources = []) => (
  [...resources].sort((left, right) => {
    const leftIndex = RESOURCE_ORDER.indexOf(normalizeResourceId(left.resource_id));
    const rightIndex = RESOURCE_ORDER.indexOf(normalizeResourceId(right.resource_id));
    const safeLeftIndex = leftIndex === -1 ? RESOURCE_ORDER.length : leftIndex;
    const safeRightIndex = rightIndex === -1 ? RESOURCE_ORDER.length : rightIndex;

    return safeLeftIndex - safeRightIndex;
  })
);

const getResourceId = (item) => (
  item?.resource_id || item?.resource || item?.id || item?.key || item?.name
);

const getResourceValue = (item) => (
  item?.quantity ?? item?.usage ?? item?.used ?? item?.remaining ?? item?.value ?? item?.amount
);

const addToMap = (map, rawResourceId, rawValue) => {
  const resourceId = normalizeResourceId(rawResourceId);
  const value = parseNumericValue(rawValue);

  if (!resourceId || !getUsageResourceLabel(resourceId) || value === null) {
    return;
  }

  map[resourceId] = (map[resourceId] ?? 0) + value;
};

const listToMap = (resources = []) => {
  const map = {};

  if (!Array.isArray(resources)) {
    return map;
  }

  resources.forEach((item) => {
    addToMap(map, getResourceId(item), getResourceValue(item));
  });

  return map;
};

const subtractIfPossible = (left, right) => {
  if (left === null || left === undefined || right === null || right === undefined) {
    return null;
  }

  return Math.max(left - right, 0);
};

const subtractIfNonNegative = (left, right) => {
  if (left === null || left === undefined || right === null || right === undefined) {
    return null;
  }

  if (right > left) {
    return null;
  }

  return left - right;
};

export const buildResourceSummaryRows = ({
  assignedResources = [],
  remainingResources = [],
  usageResources = [],
  includeUsageOnly = true,
} = {}) => {
  const assignedMap = listToMap(assignedResources);
  const remainingMap = listToMap(remainingResources);
  const usageMap = listToMap(usageResources);
  const resourceIds = Array.from(new Set([
    ...RESOURCE_ORDER,
    ...Object.keys(assignedMap),
    ...Object.keys(remainingMap),
    ...Object.keys(usageMap),
  ])).filter((resourceId) => (
    getUsageResourceLabel(resourceId) &&
    (includeUsageOnly || !USAGE_ONLY_RESOURCE_LABELS[resourceId])
  ));

  return resourceIds.map((resourceId) => {
    const usageOnly = Boolean(USAGE_ONLY_RESOURCE_LABELS[resourceId]);
    const assigned = usageOnly ? null : assignedMap[resourceId] ?? null;
    const usage = usageMap[resourceId] ?? null;
    const remaining = usageOnly ? null : remainingMap[resourceId] ?? null;
    const inferredUsed = subtractIfNonNegative(assigned, remaining);
    const used = usage ?? inferredUsed ?? (assigned === null ? null : 0);
    const calculatedRemaining = remaining ?? subtractIfPossible(assigned, used);

    return {
      resourceId,
      label: getUsageResourceLabel(resourceId),
      assigned,
      used,
      remaining: calculatedRemaining,
    };
  });
};

export const getSharedAvailableHa = (resources = [], resourceIds = []) => {
  const map = listToMap(resources);
  const values = resourceIds
    .map(resourceId => map[normalizeResourceId(resourceId)])
    .filter(value => value !== undefined && value !== null);

  return values.length ? Math.max(...values) : null;
};

// payment-api preserves the historical Specific tenant policy with a very
// large numeric sentinel instead of an explicit unlimited flag. Never expose
// that implementation detail as a misleading twelve-digit hectare balance.
export const isEffectivelyUnlimitedAvailability = value => (
  Number.isFinite(Number(value)) && Number(value) >= 900000000000
);

const getByPath = (source, path) => (
  path.split(".").reduce((value, key) => (
    value && value[key] !== undefined ? value[key] : undefined
  ), source)
);

const findDirectValue = (source, fieldNames) => {
  for (const fieldName of fieldNames) {
    const value = getByPath(source, fieldName);
    const numeric = parseNumericValue(value);

    if (numeric !== null) {
      return numeric;
    }
  }

  return null;
};

const findValueInCollections = (source, resourceIds) => {
  const collections = [
    "plan.resources",
    "company.plan.resources",
    "company_plan.resources",
    "resources",
    "assigned_resources",
  ];

  for (const collectionPath of collections) {
    const collection = getByPath(source, collectionPath);

    if (!Array.isArray(collection)) {
      continue;
    }

    const map = listToMap(collection);

    for (const resourceId of resourceIds.map(normalizeResourceId)) {
      if (map[resourceId] !== undefined) {
        return map[resourceId];
      }
    }
  }

  return null;
};

export const getUserAssignedAmbientsHa = (user) => (
  findDirectValue(user, [
    "ambients_assigned",
    "ambients_ha_assigned",
    "assigned_ambients_ha",
    "company.ambients_assigned",
    "company.assigned_ambients_ha",
  ]) ?? findValueInCollections(user, ["ambients"])
);

export const getUserAssignedReportsHa = (user) => (
  findDirectValue(user, [
    "reports_assigned",
    "reports_ha_assigned",
    "reports_ndvi_ha_assigned",
    "assigned_reports_ha",
    "assigned_reports_ndvi_ha",
    "company.reports_assigned",
    "company.reports_ndvi_ha_assigned",
    "company.assigned_reports_ndvi_ha",
  ]) ?? findValueInCollections(user, ["reports_ndvi_ha"])
);
